<template>
  <div class="mobile-product-grid">
    <div 
      v-for="product in displayProducts" 
      :key="product.PLU"
      class="product-card"
      @click="addProductToCart(product)"
    >
      <div class="product-image">
        <div class="product-image-placeholder">
          <font-awesome-icon icon="fa-solid fa-utensils" />
        </div>
      </div>
      
      <div class="product-info">
        <h4 class="product-name">{{ product.Description }}</h4>
        <div class="product-footer">
          <span class="product-price">{{ formatPrice(product.SellingIncl) }}</span>
          <button class="add-btn" @click.stop="addProductToCart(product)">
            <font-awesome-icon icon="fa-solid fa-plus" />
          </button>
        </div>
      </div>
      
      <!-- Quick Add Feedback -->
      <div class="add-feedback" :class="{ active: showFeedback[product.PLU] }">
        <font-awesome-icon icon="fa-solid fa-check" />
      </div>
    </div>
    
    <!-- Empty State -->
    <div v-if="displayProducts.length === 0" class="empty-state">
      <font-awesome-icon icon="fa-solid fa-box-open" class="empty-icon" />
      <p class="empty-text">No products found</p>
      <p class="empty-subtext">Try searching or selecting a different category</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MobileProductGrid',
  data() {
    return {
      showFeedback: {}
    };
  },
  computed: {
    displayProducts() {
      const limit = this.$store.getters.getLimit;
      // getStockProducts is a property returning an array, not a function
      return (this.$store.getters.getStockProducts || [])
        .slice(0, limit)
        .filter(item => item.archived === false)
        .sort((a, b) => a.Description.localeCompare(b.Description));
    }
  },
  methods: {
    formatPrice(price) {
      return this.$utils.formatMoney(price);
    },
    async addProductToCart(item) {
      try {
        const set = await this.$store.dispatch('getStockSetting', item.PLU);
        let cost = Number(item.InclCost.toFixed(2));
        let price = Number(item.SellingIncl.toFixed(2));
        let description = item.Description;
        let quantity = 1;

        // Handle get quantity from price
        if (set.getQtyFromPrice) {
          const { value: totalItemPrice } = await Swal.fire({
            title: 'Enter Total Item Price',
            input: 'number',
            icon: "question",
            inputLabel: 'Total Item Price',
            inputPlaceholder: 'Enter the total price for the items',
            showCancelButton: true,
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            inputValidator: (value) => {
              if (!value || isNaN(value) || value <= 0) {
                return 'Please enter a valid price!';
              }
            }
          });

          if (totalItemPrice) {
            quantity = totalItemPrice / price;
          } else {
            return; // User cancelled
          }
        }

        // Cost check
        if (cost > price) {
          const result = await Swal.fire({
            allowOutsideClick: false,
            icon: "warning",
            showCancelButton: true,
            title: 'Cost price is greater than Selling price',
            confirmButtonText: 'Proceed',
          });

          if (!result.isConfirmed) return;
        }

        // Ask for description
        if (set.askForDescription || set.askForBoth) {
          description = await this.askForDescription(item);
          if (!description) return; // User cancelled
        }

        // Ask for price
        let priceManuallySet = false;
        if (set.askForPrice || set.askForBoth) {
          price = await this.askForPrice(item);
          if (price === null) return; // User cancelled
          priceManuallySet = true;
        }

        // Tourism levy
        let tourismLevy = 0.0;
        if (set.tourism) {
          let obj = { amount: price, incl: true };
          tourismLevy = await this.$store.dispatch('getTourismLevyAmount', obj);
        }

        // Cooking instructions
        if (set.cookingLink) {
          this.$emit('open-instruction', item, set.cookingLink);
          return;
        }

        // Check for special price
        let specialPrice = this.$store.getters.getItemSpecialPrice(item.PLU);
        if (specialPrice) {
          responses.showSuccess('Special Detected');
          price = specialPrice;
        }

        // Create sale line object
        let obj = {
          Plu: item.PLU,
          Description: description,
          Qty: quantity,
          Unit: price,
          Line: price * quantity,
          Discount: 0,
          tourismLevy,
          vat: 0,
          instructions: []
        };

        // Preserve manually entered price through recalcSpecials
        if (priceManuallySet) {
          obj.baseUnit = price;
        }

        // Add to cart
        this.$store.commit('addToCurrentSaleLines', obj);
        await this.$store.dispatch('calculateTipAmount');

        // Show feedback
        this.$set(this.showFeedback, item.PLU, true);
        setTimeout(() => {
          this.$set(this.showFeedback, item.PLU, false);
        }, 600);

        // Emit event
        this.$emit('add-to-cart', item);
        
      } catch (error) {
        console.error('Error adding to cart:', error);
        responses.showError('Failed to add item to cart');
      }
    },
    askForPrice(item) {
      this.$store.commit('setIsTabOpen', true);
      return Swal.fire({
        allowOutsideClick: false,
        showCancelButton: true,
        title: 'Set Selling Price',
        html: `<h6>Description: ${item.Description}</h6>
               <h6>Current Price: ${this.$utils.formatMoney(item.SellingIncl)}</h6>
               <input type="number" step="0.01" id="selling-prc" class="swal2-input" placeholder="Selling Price" style="max-width: 70%">`,
        confirmButtonText: 'Set Price',
        focusConfirm: false,
        preConfirm: () => {
          let value = Swal.getPopup().querySelector('#selling-prc').value;
          if (!value) {
            Swal.showValidationMessage('Please enter a valid Selling Price');
          }
          return parseFloat(value);
        }
      }).then(result => {
        this.$store.commit('setIsTabOpen', false);
        if (result.isConfirmed) {
          return result.value;
        }
        return parseFloat(item.SellingIncl.toFixed(2));
      });
    },
    askForDescription(item) {
      this.$store.commit('setIsTabOpen', true);
      return Swal.fire({
        allowOutsideClick: false,
        showCancelButton: true,
        title: 'Set Description',
        html: `<h6>Description: ${item.Description}</h6>
               <textarea id="desc-ask" class="swal2-input" placeholder="Description" style="max-width: 70%"></textarea>`,
        confirmButtonText: 'Set Description',
        focusConfirm: false,
        preConfirm: () => {
          let value = Swal.getPopup().querySelector('#desc-ask').value;
          if (!value || value.toString().trim() === "") {
            Swal.showValidationMessage('Please enter a valid Description');
          }
          return value;
        }
      }).then(result => {
        this.$store.commit('setIsTabOpen', false);
        if (result.isConfirmed) {
          return result.value;
        }
        return item.Description;
      });
    }
  }
};
</script>

<style scoped lang="scss">
.mobile-product-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 12px;
  
  @media (orientation: landscape) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.product-card {
  position: relative;
  background: var(--card-bg);
  border-radius: var(--card-radius);
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  overflow: hidden;
  cursor: pointer;
  transition: var(--transition-default);
  
  &:active {
    transform: scale(0.98);
    box-shadow: var(--card-shadow-hover);
  }
}

.product-image {
  position: relative;
  width: 100%;
  padding-top: 100%; // Square aspect ratio
  background: linear-gradient(135deg, rgba(var(--accent-electric-blue-rgb), 0.1) 0%, rgba(var(--accent-neon-purple-rgb), 0.1) 100%);
  
  .product-image-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    color: rgba(var(--accent-electric-blue-rgb), 0.3);
  }
}

.product-info {
  padding: 12px;
  
  .product-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
    line-height: 1.3;
    height: 36px; // 2 lines max
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  
  .product-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    .product-price {
      font-size: 16px;
      font-weight: 700;
      color: var(--accent-primary);
    }
    
    .add-btn {
      width: 36px;
      height: 36px;
      border-radius: var(--button-radius);
      background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-primary-dark) 100%);
      color: white;
      border: none;
      font-size: 14px;
      cursor: pointer;
      transition: var(--transition-default);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(var(--accent-electric-blue-rgb), 0.3);
      
      &:active {
        transform: scale(0.9);
      }
    }
  }
}

.add-feedback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(var(--success-rgb), 0.95);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.3s ease;
  pointer-events: none;
  
  &.active {
    opacity: 1;
    transform: scale(1);
  }
}

.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  
  .empty-icon {
    font-size: 64px;
    color: var(--text-secondary);
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  .empty-text {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .empty-subtext {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
  }
}
</style>
